import React, { useState, useEffect } from 'react';
import { applicationAPI } from '../api/recruitmentApi';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const ComparisonModal = ({ applicationIds, onClose }) => {
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadComparison();
  }, []);

  const loadComparison = async () => {
    try {
      setLoading(true);
      const response = await applicationAPI.compareApplications(applicationIds);
      setComparison(response);
    } catch (error) {
      console.error('Failed to load comparison:', error);
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 4.0) return 'bg-green-100 text-green-800';
    if (score >= 3.0) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <LoadingSpinner />
      </div>
    );
  }

  if (!comparison) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold">Candidate Comparison</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        <div className="p-6">
          {/* Top Candidate Highlight */}
          {comparison.topCandidate && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-green-900 mb-2">🏆 Top Candidate</h3>
              <p className="text-green-800">
                {comparison.topCandidate.firstName} {comparison.topCandidate.lastName} - 
                Overall Score: {comparison.topCandidate.overallScore.toFixed(1)}
              </p>
            </div>
          )}

          {/* Comparison Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50">
                    Metric
                  </th>
                  {comparison.applications.map((app) => (
                    <th key={app.id} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[200px]">
                      <div>
                        <div className="font-semibold text-gray-900">
                          {app.firstName} {app.lastName}
                        </div>
                        <div className="text-xs text-gray-500 font-normal mt-1">
                          {app.email}
                        </div>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {/* Overall Score */}
                <tr className="bg-blue-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 sticky left-0 bg-blue-50">
                    Overall Score
                  </td>
                  {comparison.applications.map((app) => (
                    <td key={app.id} className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-3 py-1 rounded-full font-bold text-lg ${getScoreColor(app.overallScore)}`}>
                        {app.overallScore?.toFixed(1) || 'N/A'}
                      </span>
                    </td>
                  ))}
                </tr>

                {/* Technical Score */}
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 sticky left-0 bg-white">
                    Technical Score
                  </td>
                  {comparison.applications.map((app) => (
                    <td key={app.id} className="px-6 py-4 whitespace-nowrap text-sm">
                      {app.technicalScore?.toFixed(1) || 'N/A'}
                    </td>
                  ))}
                </tr>

                {/* Communication Score */}
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 sticky left-0 bg-gray-50">
                    Communication Score
                  </td>
                  {comparison.applications.map((app) => (
                    <td key={app.id} className="px-6 py-4 whitespace-nowrap text-sm">
                      {app.communicationScore?.toFixed(1) || 'N/A'}
                    </td>
                  ))}
                </tr>

                {/* Experience Score */}
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 sticky left-0 bg-white">
                    Experience Score
                  </td>
                  {comparison.applications.map((app) => (
                    <td key={app.id} className="px-6 py-4 whitespace-nowrap text-sm">
                      {app.experienceScore?.toFixed(1) || 'N/A'}
                    </td>
                  ))}
                </tr>

                {/* Culture Fit Score */}
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 sticky left-0 bg-gray-50">
                    Culture Fit Score
                  </td>
                  {comparison.applications.map((app) => (
                    <td key={app.id} className="px-6 py-4 whitespace-nowrap text-sm">
                      {app.cultureFitScore?.toFixed(1) || 'N/A'}
                    </td>
                  ))}
                </tr>

                {/* Status */}
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 sticky left-0 bg-white">
                    Status
                  </td>
                  {comparison.applications.map((app) => (
                    <td key={app.id} className="px-6 py-4 whitespace-nowrap text-sm">
                      {app.status?.replace('-', ' ')}
                    </td>
                  ))}
                </tr>

                {/* Years of Experience */}
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 sticky left-0 bg-gray-50">
                    Years of Experience
                  </td>
                  {comparison.applications.map((app) => (
                    <td key={app.id} className="px-6 py-4 whitespace-nowrap text-sm">
                      {app.yearsOfExperience || 'N/A'}
                    </td>
                  ))}
                </tr>

                {/* Interviews Completed */}
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 sticky left-0 bg-white">
                    Interviews Completed
                  </td>
                  {comparison.applications.map((app) => (
                    <td key={app.id} className="px-6 py-4 whitespace-nowrap text-sm">
                      {app.interviews?.filter(i => i.status === 'completed').length || 0}
                    </td>
                  ))}
                </tr>

                {/* Applied Date */}
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 sticky left-0 bg-gray-50">
                    Applied Date
                  </td>
                  {comparison.applications.map((app) => (
                    <td key={app.id} className="px-6 py-4 whitespace-nowrap text-sm">
                      {new Date(app.createdAt).toLocaleDateString()}
                    </td>
                  ))}
                </tr>

                {/* Resume */}
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 sticky left-0 bg-white">
                    Resume
                  </td>
                  {comparison.applications.map((app) => (
                    <td key={app.id} className="px-6 py-4 whitespace-nowrap text-sm">
                      {app.resumePath ? (
                        <a
                          href={app.resumePath}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800"
                        >
                          View Resume
                        </a>
                      ) : (
                        'N/A'
                      )}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>

          {/* Ranking */}
          {comparison.ranking && comparison.ranking.length > 0 && (
            <div className="mt-6 bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-3">Ranking by Overall Score</h3>
              <ol className="space-y-2">
                {comparison.ranking.map((item, index) => (
                  <li key={item.id} className="flex items-center gap-3">
                    <span className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                      index === 0 ? 'bg-yellow-400 text-yellow-900' :
                      index === 1 ? 'bg-gray-300 text-gray-900' :
                      index === 2 ? 'bg-orange-300 text-orange-900' :
                      'bg-gray-200 text-gray-700'
                    }`}>
                      {index + 1}
                    </span>
                    <span className="flex-1">
                      {item.name} - <span className="font-semibold">{item.score.toFixed(1)}</span>
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {/* Close Button */}
          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComparisonModal;
